import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { resolveImage } from "@/lib/api";

const FeaturedVideo = ({ video }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    if (!ref.current) return;
    ref.current.muted = !ref.current.muted;
    setMuted(ref.current.muted);
    if (!ref.current.muted) ref.current.play().catch(() => {});
  };

  return (
    <motion.div
      data-testid="featured-video"
      initial={{ opacity: 0, scale: 0.98 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className="relative w-full mb-6 border border-white/10 overflow-hidden bg-black"
    >
      <div className="relative w-full aspect-video max-h-[80vh] bg-black">
        <video
          ref={ref}
          src={resolveImage(video.url)}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 flex items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-2 bg-ad-red text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 mb-3">
            Now Playing
          </span>
          <h3 className="font-display text-3xl sm:text-5xl uppercase tracking-tight text-white leading-none">
            {video.title || "Inside American Dream"}
          </h3>
        </div>
        <button
          onClick={toggleMute}
          data-testid="featured-video-mute"
          className="shrink-0 h-12 w-12 grid place-items-center bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-ad-red hover:border-ad-red transition-colors"
          title={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>
    </motion.div>
  );
};

export const VideosSection = ({ videos }) => {
  if (!videos?.length) return null;
  const featured = videos.find((v) => v.featured) || videos[0];
  const rest = videos.filter((v) => v.id !== featured.id);

  return (
    <section id="showroom" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-ad-surface border-y border-white/10">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="text-ad-red text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-ad-red" /> See It In Action
          </p>
          <h2 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tight">
            Inside The <span className="metallic-text">Showroom</span>
          </h2>
        </motion.div>

        <FeaturedVideo video={featured} />

        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {rest.map((v, i) => (
              <motion.div
                key={v.id}
                data-testid={`video-${v.id}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="group relative bg-black border border-white/10 overflow-hidden"
              >
                <div className="h-[420px] w-full bg-black grid place-items-center">
                  <video
                    src={resolveImage(v.url)}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-contain"
                  />
                </div>
                {v.title && (
                  <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none bg-gradient-to-b from-black/70 to-transparent">
                    <span className="font-display text-lg uppercase tracking-wide text-white">{v.title}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
